import { ref } from "vue";
import { defineStore } from "pinia";
import { useToast } from "vue-toastification";
import * as Sentry from "@sentry/vue";

import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  GetProjectRevisionsDocument,
  UpdateProjectRevisionDocument,
  UpdateProjectRevisionWithVersionDocument,
  type GetProjectRevisionsQuery,
  type GetProjectRevisionsQueryVariables,
  type ProjectRevision_Set_Input,
  type UpdateProjectRevisionMutation,
  type UpdateProjectRevisionMutationVariables,
  type UpdateProjectRevisionWithVersionMutation,
  type UpdateProjectRevisionWithVersionMutationVariables,
} from "@/graphql";
import { activateProjectRevision, createProjectRevision, createProjectRevisionFromVersion } from "@/api/services/ProjectRevision";
import { toTransportTypes } from "@/dataProviders/ProjectWrapper/services/transportTypesFromHasura";
import { actorFrom, resolveProjectActorDirectory } from "../services/projectActors";
import { isVersioningSchemaMissing } from "../services/versioningSchema";
import { buildTrailScope, type TrailScope } from "../services/versionsScopedToRevision";
import type {
  ProjectRevision,
  ProjectRevisionLocation,
  ProjectRevisionScalars,
  ProjectRevisionModel,
  ProjectRevisionOperational,
  ProjectRevisionTransport,
} from "../types/projectRevision.types";

// useProjectRevisionsStore — the single source of truth for named revisions + the current pointer;
// see .claude/rules/versioning.md § "The one store that rules them all" and "The ownership rule".

export interface ActiveRevisionMeta {
  id: string;
  name: string;
}

/** A revision's own calculation stage labels, or null when there's nothing to say (no calc row, or no stages) — empty must degrade to "unknown", not "this revision calculated no stages". */
const stageLabelsOrNull = (calculation: { stages: Array<{ stage: { label: string } }> } | null | undefined): string[] | null => {
  const labels = (calculation?.stages ?? []).map((row) => row.stage.label);
  return labels.length > 0 ? labels : null;
};

/** A revision's own frozen scalars, or null when it captured none. buildingTypeId is the NOT-NULL marker for "were scalars
 * captured"; the whole object degrades to null together so a form never renders half-blank. */
const revisionScalarsOrNull = (row: {
  projectNumber?: string | null;
  clientName?: string | null;
  builderName?: string | null;
  lcaResponsibleName?: string | null;
  numberOfPeople?: number | null;
  buildingTypeId?: number | null;
  projectStatusId?: number | null;
  yearOfConstruction?: number | null;
  yearOfCommissioning?: number | null;
}): ProjectRevisionScalars | null => {
  if (row.buildingTypeId === null || row.buildingTypeId === undefined) return null;

  return {
    projectNumber: row.projectNumber ?? null,
    clientName: row.clientName ?? null,
    builderName: row.builderName ?? null,
    lcaResponsibleName: row.lcaResponsibleName ?? null,
    numberOfPeople: row.numberOfPeople ?? null,
    buildingTypeId: row.buildingTypeId,
    projectStatusId: row.projectStatusId ?? null,
    yearOfConstruction: row.yearOfConstruction ?? null,
    yearOfCommissioning: row.yearOfCommissioning ?? null,
  };
};

/** A revision's own projectModel rows for the 3D viewer, or null when there's nothing to say — empty collapses to null for the same reason as stageLabelsOrNull; see versioning.md § "The one store that rules them all". */
const revisionModelsOrNull = (rows: ReadonlyArray<ProjectRevisionModel> | null | undefined): ProjectRevisionModel[] | null => {
  const models = (rows ?? []).map((row) => ({
    name: row.name,
    referencedObjectId: row.referencedObjectId ?? null,
    commitId: row.commitId ?? null,
    speckleModelId: row.speckleModelId ?? null,
  }));
  return models.length > 0 ? models : null;
};

/** Revision's own transport rows for the Transport (A4) step, classified via the same toTransportTypes as the working-state read; empty deliberately stays empty (not null) — see versioning.md § "The one store that rules them all" (readTransportsFor). */
const revisionTransportsOrNull = (
  rows: ReadonlyArray<ProjectRevisionTransport> | null | undefined,
  pointers: ReadonlyArray<{ project_transport_id: string }> | null | undefined
): ProjectRevisionTransport[] | null =>
  rows ? toTransportTypes({ project_transport: rows, project_transport_default: pointers ?? [] }) : null;

export const useProjectRevisionsStore = defineStore("projectRevisions", () => {
  const toast = useToast();

  const revisionsByProject = ref<Record<string, ProjectRevision[]>>({});
  const activeMetaByProject = ref<Record<string, ActiveRevisionMeta | null>>({});
  const viewingIdByProject = ref<Record<string, string | null>>({});
  const loadingByProject = ref<Record<string, boolean>>({});
  const unavailableByProject = ref<Record<string, boolean>>({});
  // First pointer emission per project seeds silently; later changes get announced (they mean an
  // admin — possibly someone else — switched the current revision under everyone's feet).
  const pointerSeeded = ref<Record<string, boolean>>({});

  // --- getters (functions — call with a projectId) ---

  /** The DISPLAY list — every surface that renders revisions reads this. See versioning.md § "The trail is a TREE displayed one branch at a time" for why the unfiltered rows survive in revisionsByProject. */
  const revisionsFor = (projectId: string): ProjectRevision[] =>
    (revisionsByProject.value[projectId] ?? []).filter((revision) => revision.deletedAt === null);

  const activeMetaFor = (projectId: string): ActiveRevisionMeta | null => activeMetaByProject.value[projectId] ?? null;

  /** The revision the user is looking at: their preview if set, else the current one's row. */
  const viewingRevisionFor = (projectId: string): ProjectRevision | null => {
    const revisions = revisionsFor(projectId);
    const viewingId = viewingIdByProject.value[projectId];
    if (viewingId) {
      const match = revisions.find((revision) => revision.id === viewingId);
      if (match) return match;
    }
    const activeId = activeMetaFor(projectId)?.id;
    return revisions.find((revision) => revision.id === activeId) ?? null;
  };

  const isPreviewing = (projectId: string): boolean => {
    const viewingId = viewingIdByProject.value[projectId];
    return !!viewingId && viewingId !== activeMetaFor(projectId)?.id;
  };

  const isLoading = (projectId: string): boolean => loadingByProject.value[projectId] ?? false;
  const isUnavailable = (projectId: string): boolean => unavailableByProject.value[projectId] ?? false;

  /** Preview-only revision id (banner, preview watchers) — NOT what reads pin to; use readPinRevisionIdFor for fetches. See versioning.md § "The one store that rules them all". */
  const previewedRevisionIdFor = (projectId: string): string | null =>
    isPreviewing(projectId) ? viewingIdByProject.value[projectId] ?? null : null;

  /** The revision id every read pins to, or null for the live head — see versioning.md § "The ownership rule" for the reasoning. */
  const readPinRevisionIdFor = (projectId: string): string | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.id : null;
  };

  /** Project address to DISPLAY, or null for the live one — the revision's own project_location row, frozen-only per versioning.md § "The one store that rules them all". */
  const readLocationFor = (projectId: string): ProjectRevisionLocation | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.location : null;
  };

  /** Project scalars to DISPLAY (client name, project number, years), or null for the live row — null also covers a frozen revision that captured none (see revisionScalarsOrNull), so callers need no second check. Write surfaces must still gate on isPreviewing, not this pin (see versioning.md § "The settings page while previewing"). */
  const readProjectScalarsFor = (projectId: string): ProjectRevisionScalars | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.scalars : null;
  };

  /** Operational energy supply (B6) to DISPLAY, or null for live values — same frozen-only rule as readLocationFor; the write-surface overlay pattern is documented in versioning.md § "The settings page while previewing". */
  const readOperationalFor = (projectId: string): ProjectRevisionOperational | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.operational : null;
  };

  /** Lifecycle stages to DISPLAY, or null for the live ones (the REST project payload takes no revisionId). frozenAt-gated because the current revision's calculation row can go stale — a base-info save doesn't reload the revision list. */
  const readLifecycleStagesFor = (projectId: string): string[] | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.lifecycleStages : null;
  };

  /** Models to LOAD IN THE 3D VIEWER, or null for the live rows. See versioning.md § "The one store that rules them all" — the 3D viewer paragraph explains why this reads project_model directly. */
  const readModelsFor = (projectId: string): ProjectRevisionModel[] | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.models : null;
  };

  /** Transport types (A4) to DISPLAY. See versioning.md § "The one store that rules them all" — readTransportsFor's empty-list and frozen-only rules are documented there. */
  const readTransportsFor = (projectId: string): ProjectRevisionTransport[] | null => {
    const revision = viewingRevisionFor(projectId);
    return revision?.frozenAt ? revision.transports : null;
  };

  /** Trail truncation instant for display (null shows all). frozenAt records only the LAST freeze, so a project that switched back and forth may still show changes made on a different revision during the gap — exact only for a simple save-save-save-then-preview history. */
  const readTrailCutoffFor = (projectId: string): string | null => viewingRevisionFor(projectId)?.frozenAt ?? null;

  /** The viewed revision's branch scope for the trail; see versioning.md § "The trail is a TREE displayed one branch at a time" (trailScopeFor / versionsScopedToRevision). */
  const trailScopeFor = (projectId: string): TrailScope | null =>
    buildTrailScope(viewingRevisionFor(projectId)?.id ?? null, revisionsByProject.value[projectId] ?? []);

  /** Project name to DISPLAY, or null to use the live one. project_revision.project_name only refreshes when frozen —
   * getting this wrong showed post-rename staleness and a restored project's source name instead of the given one. */
  const displayProjectNameFor = (projectId: string, liveName: string): string => {
    const revision = viewingRevisionFor(projectId);
    return (revision?.frozenAt ? revision.projectName : null) ?? liveName;
  };

  /** Gate for the pointer subscription: only subscribe where the schema exists. */
  const isSubscribable = (projectId: string): boolean =>
    !!projectId && projectId in revisionsByProject.value && !isUnavailable(projectId);

  // --- actions ---

  /** Resolves false when the read did not land, so a manually-triggered caller (History's Refresh) can report failure instead of showing a stale list as fresh. */
  const loadRevisions = async (projectId: string): Promise<boolean> => {
    if (!projectId) return false;
    loadingByProject.value[projectId] = true;
    try {
      const [result, actors] = await Promise.all([
        gaiupApolloClient.query<GetProjectRevisionsQuery, GetProjectRevisionsQueryVariables>({
          query: GetProjectRevisionsDocument,
          fetchPolicy: "network-only",
          context: { tolerateMissingSchema: true },
          variables: { projectId },
        }),
        resolveProjectActorDirectory(),
      ]);

      revisionsByProject.value[projectId] = (result.data?.projectRevision ?? []).map((row) => ({
        id: row.id,
        projectId,
        name: row.name,
        projectName: row.projectName ?? null,
        versionNumber: row.versionNumber,
        parentRevisionId: row.parentRevisionId ?? null,
        deletedAt: row.deletedAt ?? null,
        martenVersion: row.martenVersion,
        createdBy: actorFrom(actors, row.createdBy),
        createdAt: row.createdAt,
        // Null = this is the current revision, still moving. Everything that decides what a
        // revision shows hangs off this one field.
        frozenAt: row.frozenAt ?? null,
        // The revision's own child rows, via relationships — aliased in the query to these shapes
        location: (row.location as ProjectRevisionLocation | null) ?? null,
        // The project row's own frozen columns — see `readProjectScalarsFor` for the null rule.
        scalars: revisionScalarsOrNull(row),
        operational: (row.operational as ProjectRevisionOperational | null) ?? null,
        // Empty collapses to null here (see stageLabelsOrNull) so "no calculation row" and "no stages" read as one unknown state.
        lifecycleStages: stageLabelsOrNull(row.calculation),
        // The revision's own geometry pins — see `readModelsFor`.
        models: revisionModelsOrNull(row.models),
        // The revision's own transport rows, classified by its own Default pointer — see
        // `readTransportsFor`. Empty stays empty here, unlike the fields above.
        transports: revisionTransportsOrNull(row.transports, row.transportDefaults),
      }));
      unavailableByProject.value[projectId] = false;
      return true;
    } catch (e) {
      if (isVersioningSchemaMissing(e)) {
        revisionsByProject.value[projectId] = [];
        unavailableByProject.value[projectId] = true;
      } else {
        Sentry.captureException(e);
      }
      return false;
    } finally {
      loadingByProject.value[projectId] = false;
    }
  };

  /** Fed by ProjectDetailLayout's ProjectActiveRevision subscription (initial + live). */
  const applyActivePointer = (projectId: string, meta: ActiveRevisionMeta | null): void => {
    const previous = activeMetaByProject.value[projectId] ?? null;
    activeMetaByProject.value[projectId] = meta;

    if (viewingIdByProject.value[projectId] === meta?.id) {
      viewingIdByProject.value[projectId] = null;
    }

    if (meta && !revisionsFor(projectId).some((revision) => revision.id === meta.id)) {
      void loadRevisions(projectId);
    }

    const seeded = pointerSeeded.value[projectId] ?? false;
    pointerSeeded.value[projectId] = true;
    if (seeded && meta && meta.id !== previous?.id) {
      toast.success(`Current revision changed to “${meta.name}”.`);
    }
  };

  /** Admin action — the backend copy-on-writes, pins materials and auto-activates in one transaction. */
  const saveRevision = async (projectId: string, name: string): Promise<string> => {
    const revisionId = await createProjectRevision(projectId, name);
    await loadRevisions(projectId);
    const saved = revisionsFor(projectId).find((revision) => revision.id === revisionId);
    if (saved) applyActivePointer(projectId, { id: saved.id, name: saved.name });
    viewingIdByProject.value[projectId] = null;
    return revisionId;
  };

  /** Admin action — "Save as revision" without switching; see versioning.md § "Saving and switching" (project_revision_save_frozen). */
  const saveRevisionFromVersion = async (projectId: string, versionId: string, name: string): Promise<string> => {
    const revisionId = await createProjectRevisionFromVersion(projectId, versionId, name);
    await loadRevisions(projectId);
    return revisionId;
  };

  /** Admin action — switches to another save point; see versioning.md § "Saving and switching — the revision Postgres functions" for what the backend does. */
  const activateRevision = async (projectId: string, revisionId: string): Promise<void> => {
    await activateProjectRevision(projectId, revisionId);
    viewingIdByProject.value[projectId] = null;
    await loadRevisions(projectId);
  };

  /** Rename and delete share this writer: both land on the same Hasura update permission, which excludes frozenAt/the Marten pin/scalars, so it can only touch a revision's label and visibility — admin-enforced by the permission's own membership filter. */
  const updateRevision = async (
    projectId: string,
    revisionId: string,
    set: ProjectRevision_Set_Input,
    summary: string,
    entityLabel: string
  ): Promise<void> => {
    const version = {
      projectId,
      entityType: "project",
      operation: "update",
      summary,
      entityId: revisionId,
      entityLabel,
      changes: [],
    };

    let affectedRows: number | undefined;

    try {
      const result = await gaiupApolloClient.mutate<
        UpdateProjectRevisionWithVersionMutation,
        UpdateProjectRevisionWithVersionMutationVariables
      >({
        mutation: UpdateProjectRevisionWithVersionDocument,
        variables: { id: revisionId, set, version },
      });
      affectedRows = result.data?.update_projectRevision?.affected_rows;
    } catch (mutationError) {
      if (!isVersioningSchemaMissing(mutationError)) throw mutationError;
      const result = await gaiupApolloClient.mutate<UpdateProjectRevisionMutation, UpdateProjectRevisionMutationVariables>({
        mutation: UpdateProjectRevisionDocument,
        variables: { id: revisionId, set },
      });
      affectedRows = result.data?.update_projectRevision?.affected_rows;
    }

    // A non-admin caller matches no row; Hasura reports that as a successful mutation over zero rows,
    // so the refusal has to be turned back into an error here.
    if (!affectedRows) throw new Error(`Revision ${revisionId} was not updated — the server matched no row.`);

    await loadRevisions(projectId);
  };

  /** Admin action — rename a save point. Allowed on the current revision too; only its label moves. */
  const renameRevision = async (projectId: string, revisionId: string, name: string): Promise<void> => {
    const trimmedName = name.trim();
    await updateRevision(projectId, revisionId, { name: trimmedName }, `Revision renamed to “${trimmedName}”`, trimmedName);

    // The pointer subscription only re-emits when the pointer MOVES, so renaming the current
    // revision needs its cached name patched here or the toolbar shows the old one until navigation.
    const active = activeMetaFor(projectId);
    if (active?.id === revisionId) activeMetaByProject.value[projectId] = { ...active, name: trimmedName };
  };

  /** Admin action — hides a save point without destroying it (`deletedAt`/`deletedBy` are trigger-stamped, not client-sent);
   * the server refuses to hide the revision the project is currently on. */
  const deleteRevision = async (projectId: string, revisionId: string, name: string): Promise<void> => {
    await updateRevision(projectId, revisionId, { deletedAt: new Date().toISOString() }, `Revision “${name}” deleted`, name);

    if (viewingIdByProject.value[projectId] === revisionId) viewingIdByProject.value[projectId] = null;
  };

  /** Client-local preview. Pass null to follow the current revision again. */
  const setViewingRevision = (projectId: string, revisionId: string | null): void => {
    viewingIdByProject.value[projectId] = revisionId && revisionId !== activeMetaFor(projectId)?.id ? revisionId : null;
  };

  return {
    // state
    revisionsByProject,
    activeMetaByProject,
    viewingIdByProject,

    // getters (functions)
    revisionsFor,
    activeMetaFor,
    viewingRevisionFor,
    isPreviewing,
    isLoading,
    isUnavailable,
    isSubscribable,
    previewedRevisionIdFor,
    readPinRevisionIdFor,
    readLocationFor,
    readProjectScalarsFor,
    readOperationalFor,
    readLifecycleStagesFor,
    readModelsFor,
    readTransportsFor,
    readTrailCutoffFor,
    trailScopeFor,
    displayProjectNameFor,

    // actions
    loadRevisions,
    applyActivePointer,
    saveRevision,
    saveRevisionFromVersion,
    activateRevision,
    renameRevision,
    deleteRevision,
    setViewingRevision,
  };
});

export default useProjectRevisionsStore;
